import {
  type Change,
  type ChangeKind,
  changePersonId,
  type HireReason,
  isSquadChange,
  totalCost,
} from '@tive/core';
import {
  ArrowRight,
  Boxes,
  Building2,
  CheckCircle2,
  ChevronDown,
  GitBranch,
  LogOut,
  MapPin,
  MoveRight,
  Table2,
  Tag,
  Trash2,
  Undo2,
  UserPlus,
  Users,
} from 'lucide-react';
import { useMemo } from 'react';
import { cn } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { SecretAmount } from './SecretAmount';
import { Badge, Button, EmptyState, IconButton } from './ui';

const GROUPS: {
  kind: ChangeKind;
  label: string;
  icon: typeof MoveRight;
  tone: 'moved' | 'fresh' | 'brand' | 'leaving' | 'neutral';
}[] = [
  { kind: 'move', label: 'Reporting changes', icon: MoveRight, tone: 'moved' },
  { kind: 'hire', label: 'New roles', icon: UserPlus, tone: 'fresh' },
  { kind: 'retitle', label: 'Title changes', icon: Tag, tone: 'brand' },
  { kind: 'department', label: 'Department changes', icon: Building2, tone: 'neutral' },
  { kind: 'location', label: 'Location changes', icon: MapPin, tone: 'neutral' },
  { kind: 'dotted', label: 'Dotted lines', icon: GitBranch, tone: 'brand' },
  { kind: 'departure', label: 'Departures', icon: LogOut, tone: 'leaving' },
  { kind: 'squad-add', label: 'New teams', icon: Boxes, tone: 'fresh' },
  { kind: 'squad-member', label: 'Team moves', icon: Users, tone: 'brand' },
  { kind: 'squad-rename', label: 'Team renames', icon: Tag, tone: 'neutral' },
  { kind: 'squad-disband', label: 'Teams standing down', icon: LogOut, tone: 'leaving' },
];

export function ChangesList() {
  const changes = useAppStore((s) => s.changes);
  const summary = useAppStore((s) => s.summary);
  const scenario = useAppStore((s) => s.scenario);
  const applyOps = useAppStore((s) => s.applyOps);
  const revealPerson = useAppStore((s) => s.revealPerson);
  const setView = useAppStore((s) => s.setView);
  const exportScenario = useAppStore((s) => s.exportScenario);
  const setChangesTableOpen = useAppStore((s) => s.setChangesTableOpen);
  const folded = useAppStore((s) => s.foldedChangeKinds);
  const toggleChangeGroup = useAppStore((s) => s.toggleChangeGroup);

  /*
   * A change is only useful if you can see what it did. A person change pans
   * the chart to them; a team change switches to the view where the team is,
   * since a new team has no card to pan to.
   */
  const reveal = (change: Change) => {
    if (isSquadChange(change)) setView('teams');
    const personId = changePersonId(change);
    if (personId) revealPerson(personId);
  };

  const grouped = useMemo(() => {
    const map = new Map<ChangeKind, Change[]>();
    for (const change of changes) {
      const list = map.get(change.kind) ?? [];
      list.push(change);
      map.set(change.kind, list);
    }
    return map;
  }, [changes]);

  if (changes.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 size={18} />}
        title="No changes yet"
        hint="Drag a card onto another person to change who they report to, or use the + button on a card to add a role you plan to hire."
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="grid grid-cols-3 gap-2 border-b border-[var(--color-line)] px-3 py-3">
        <Stat label="Changes" value={summary.total} />
        <Stat label="People moved" value={summary.moves} tone="moved" />
        <Stat
          label="Headcount"
          value={`${summary.headcountDelta > 0 ? '+' : ''}${summary.headcountDelta}`}
          tone={summary.headcountDelta > 0 ? 'fresh' : summary.headcountDelta < 0 ? 'leaving' : 'neutral'}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {GROUPS.map((group) => {
          const items = grouped.get(group.kind);
          if (!items || items.length === 0) return null;
          const Icon = group.icon;

          const shut = folded.includes(group.kind);

          return (
            <section key={group.kind} className="mb-3">
              {/*
                The heading folds the category away. Thirty changes across six
                categories is a scroll; the ones you are not working on are the
                ones worth hiding, and which those are is nobody else`s guess.

                The total stays on the heading either way — a folded category
                still has to say how much is behind it. `HireCost` stays outside
                the button, because the figure it holds is itself a button.
              */}
              <div className="flex items-center gap-2 pr-1.5">
                <button
                  type="button"
                  onClick={() => toggleChangeGroup(group.kind)}
                  aria-expanded={!shut}
                  title={shut ? `Show ${group.label.toLowerCase()}` : `Hide ${group.label.toLowerCase()}`}
                  className={cn(
                    'flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1 text-left',
                    'transition-colors hover:bg-[var(--color-surface-2)]',
                  )}
                >
                  <Icon size={13} className="shrink-0 text-[var(--color-ink-3)]" />
                  <h3 className="truncate text-[10.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
                    {group.label}
                  </h3>
                  <Badge tone={group.tone}>{items.length}</Badge>
                  <ChevronDown
                    size={12}
                    className={cn(
                      'ml-auto shrink-0 text-[var(--color-ink-3)] transition-transform',
                      shut && '-rotate-90',
                    )}
                  />
                </button>
                {group.kind === 'hire' ? <HireCost /> : null}
              </div>

              {/*
                Gone rather than merely hidden: folded means folded, and a list
                that is still in the page is still in the tab order and still
                read out.
              */}
              {shut ? null : (
                <ul className="space-y-1">
                  {items.map((change) => (
                    <li key={change.id}>
                      <div className="group flex items-start gap-2 rounded-lg border border-transparent px-1.5 py-1.5 transition-colors hover:border-[var(--color-line)] hover:bg-[var(--color-surface-2)]">
                        <button
                          type="button"
                          onClick={() => reveal(change)}
                          className="min-w-0 flex-1 text-left"
                          title={isSquadChange(change) ? 'Show in the team view' : 'Show on the chart'}
                        >
                          <p className="truncate text-[12.5px] font-medium text-[var(--color-ink)]">
                            {titleOf(change)}
                          </p>
                          <ChangeDetail change={change} />
                        </button>
                        <IconButton
                          label="Undo this change"
                          className="size-6 opacity-0 group-hover:opacity-100 hover:text-[var(--color-moved)]"
                          onClick={() => void applyOps([{ op: 'revert', changeId: change.id }])}
                        >
                          {change.kind === 'hire' || change.kind === 'squad-add' ? (
                            <Trash2 size={12} />
                          ) : (
                            <Undo2 size={12} />
                          )}
                        </IconButton>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      <div className="flex items-center gap-2 border-t border-[var(--color-line)] px-3 py-2.5">
        {/*
          The panel is a narrow column, which is right for working and wrong for
          reading thirty changes or handing them to somebody. This opens the same
          list as a table, with room and a way out of the app.
        */}
        <Button variant="outline" size="sm" className="flex-1" onClick={() => setChangesTableOpen(true)}>
          <Table2 size={13} />
          Table
        </Button>
        <Button variant="outline" size="sm" className="flex-1" onClick={() => void exportScenario()}>
          Export JSON
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-[var(--color-moved)]"
          onClick={() => {
            if (window.confirm(`Discard all ${summary.total} change(s) in “${scenario?.name}”?`)) {
              void applyOps([{ op: 'clear' }]);
            }
          }}
        >
          Discard all
        </Button>
      </div>
    </div>
  );
}

/** A team change is headed by its team, except a move, which is about a person. */
function titleOf(change: Change): string {
  if (!isSquadChange(change)) return change.personName;
  return change.kind === 'squad-member' ? change.personName : change.squadName;
}

/** The one text a reason carries, whichever of the two kinds it is. */
function reasonText(reason: HireReason): string {
  return reason.kind === 'backfill' ? reason.backfillFor : reason.justification;
}

function ChangeDetail({ change }: { change: Change }) {
  const base = 'mt-0.5 flex items-center gap-1 text-[11px] text-[var(--color-ink-2)]';

  switch (change.kind) {
    case 'move':
      return (
        <p className={base}>
          <span className="truncate line-through decoration-[var(--color-ink-3)]/60">
            {change.fromManagerName ?? 'no manager'}
          </span>
          <ArrowRight size={10} className="shrink-0 text-[var(--color-moved)]" />
          <span className="truncate font-medium text-[var(--color-ink)]">
            {change.toManagerName ?? 'no manager'}
          </span>
          {change.movedWith > 0 ? (
            <span className="shrink-0 text-[var(--color-ink-3)]">+{change.movedWith}</span>
          ) : null}
        </p>
      );
    case 'hire':
      return (
        <>
          <p className={base}>
            <span className="truncate">{change.personTitle}</span>
            <span className="text-[var(--color-ink-3)]">under</span>
            <span className="truncate font-medium text-[var(--color-ink)]">{change.managerName ?? '—'}</span>
          </p>
          {/*
            Net new or a backfill, on its own line: it is the part somebody
            reviewing a headcount plan is actually reading, and squeezing it
            onto the line above would truncate both. The table view has room
            for the whole case; this is the gist.
          */}
          {change.reason ? (
            <p className={cn(base, 'text-[var(--color-ink-3)]')}>
              <span className="shrink-0 font-semibold">
                {change.reason.kind === 'backfill' ? 'Backfill' : 'Net new'}
              </span>
              {reasonText(change.reason) ? (
                <span className="truncate" title={reasonText(change.reason)}>
                  {reasonText(change.reason)}
                </span>
              ) : null}
            </p>
          ) : null}
        </>
      );
    case 'retitle':
      return (
        <p className={base}>
          <span className="truncate line-through decoration-[var(--color-ink-3)]/60">{change.fromTitle}</span>
          <ArrowRight size={10} className="shrink-0 text-[var(--color-brand)]" />
          <span className="truncate font-medium text-[var(--color-ink)]">{change.toTitle}</span>
        </p>
      );
    case 'department':
      return (
        <p className={base}>
          <span className="truncate line-through decoration-[var(--color-ink-3)]/60">
            {change.fromDepartment ?? 'none'}
          </span>
          <ArrowRight size={10} className="shrink-0" />
          <span className="truncate font-medium text-[var(--color-ink)]">
            {change.toDepartment ?? 'none'}
          </span>
        </p>
      );
    case 'location':
      return (
        <p className={base}>
          <span className="truncate line-through decoration-[var(--color-ink-3)]/60">
            {change.fromLocation ?? 'nowhere'}
          </span>
          <ArrowRight size={10} className="shrink-0" />
          <span className="truncate font-medium text-[var(--color-ink)]">
            {change.toLocation ?? 'nowhere'}
          </span>
        </p>
      );
    case 'dotted':
      return (
        <p className={base}>
          <span className="shrink-0 text-[var(--color-ink-3)]">also answers to</span>
          <span className="truncate font-medium text-[var(--color-ink)]" title={change.toNames.join(', ')}>
            {change.toNames.join(', ') || 'nobody'}
          </span>
        </p>
      );
    case 'departure':
      return (
        <p className={cn(base, 'text-[var(--color-leaving)]')}>
          Leaving
          {change.directReports > 0 ? ` · ${change.directReports} report(s) to reassign` : ''}
        </p>
      );
    case 'squad-add':
      return (
        <p className={cn(base, 'text-[var(--color-fresh)]')}>
          New team · {change.memberCount} member{change.memberCount === 1 ? '' : 's'}
        </p>
      );
    case 'squad-disband':
      return (
        <p className={cn(base, 'text-[var(--color-leaving)]')}>
          Standing down
          {change.memberCount > 0 ? ` · ${change.memberCount} member(s) to place` : ''}
        </p>
      );
    case 'squad-rename':
      return (
        <p className={base}>
          <span className="truncate line-through decoration-[var(--color-ink-3)]/60">{change.fromName}</span>
          <ArrowRight size={10} className="shrink-0" />
          <span className="truncate font-medium text-[var(--color-ink)]">{change.toName}</span>
        </p>
      );
    case 'squad-member':
      return (
        <p className={base}>
          {change.toRole === null ? (
            <>
              <span className="truncate text-[var(--color-leaving)]">leaves</span>
              <span className="truncate font-medium text-[var(--color-ink)]">{change.squadName}</span>
            </>
          ) : change.fromRole === null ? (
            <>
              <span className="truncate text-[var(--color-fresh)]">joins</span>
              <span className="truncate font-medium text-[var(--color-ink)]">{change.squadName}</span>
              <span className="shrink-0 text-[var(--color-ink-3)]">as {change.toRole}</span>
            </>
          ) : (
            <>
              <span className="truncate">{change.squadName}:</span>
              <span className="truncate line-through decoration-[var(--color-ink-3)]/60">
                {change.fromRole}
              </span>
              <ArrowRight size={10} className="shrink-0" />
              <span className="truncate font-medium text-[var(--color-ink)]">{change.toRole}</span>
            </>
          )}
        </p>
      );
    default:
      return null;
  }
}

function Stat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number | string;
  tone?: 'neutral' | 'moved' | 'fresh' | 'leaving';
}) {
  const tones = {
    neutral: 'text-[var(--color-ink)]',
    moved: 'text-[var(--color-moved)]',
    fresh: 'text-[var(--color-fresh)]',
    leaving: 'text-[var(--color-leaving)]',
  };
  return (
    <div className="rounded-lg bg-[var(--color-surface-2)] px-2 py-1.5">
      <p className={cn('text-[16px] leading-tight font-semibold tabular-nums', tones[tone])}>{value}</p>
      <p className="text-[10px] tracking-wide text-[var(--color-ink-3)] uppercase">{label}</p>
    </div>
  );
}

/**
 * What the new roles in this plan add up to.
 *
 * The number people actually want from a headcount plan, and the reason the
 * salary field exists at all. Approximate in two ways at once and it says so:
 * the figures are estimates, and anything not already in dollars is converted at
 * a hand-maintained rate.
 *
 * Covered like every other figure — a total is the most sensitive number on the
 * page, not the least.
 */
function HireCost() {
  /*
   * The model, not a filtered list. A selector that builds an array returns a
   * new one every call, and zustand compares by reference -- which re-renders,
   * which selects again, until React gives up and says the update depth was
   * exceeded. Deriving after the selector is what keeps it stable.
   *
   * Off the model rather than the change list because a cost belongs to the
   * role; the change entry only says one was added.
   */
  const model = useAppStore((s) => s.model);
  const total = totalCost((model?.people ?? []).filter((p) => p.isNew).map((p) => p.plannedCost));

  if (total.counted === 0) return null;

  return (
    <span className="ml-auto flex items-center gap-1.5">
      <span className="text-[10px] text-[var(--color-ink-3)]">
        {total.unconverted.length > 0 ? '≈ USD, some not converted' : '≈ USD'}
      </span>
      <SecretAmount cost={{ amount: total.usd, currency: 'USD' }} />
    </span>
  );
}
